import { Injectable } from '@angular/core';
import { downloadFile } from '@core/util/file';
import { Filters } from '@fnModels/matter-list';
import { MatterFilterFunctions } from '@functions/matter-filter.service';
import { BusinessUserRole } from '@models/businesses';
import { Matter } from '@models/matter';
import {
	Action,
	NgxsOnInit,
	Selector,
	State,
	StateContext,
	Store,
} from '@ngxs/store';
import { BusinessState } from '@state/business';
import { BusinessUsersState } from '@state/business-users/business-users.state';
import { endOfWeek, format, startOfWeek } from 'date-fns';
import LogRocket from 'logrocket';
import { filter, take } from 'rxjs/operators';
import { SnackbarService } from '../../services/ui/snackbar.service';
import * as actions from './matters-list-overview.actions';

export interface MatterListStateModel {
	selected: string;
	connected: boolean;
	loading: boolean;
	matters: Matter[];
	filters: Filters;
	error: boolean;
	downloadingCSV: boolean;
}
@State<MatterListStateModel>({
	name: 'mattersOverview',
	defaults: {
		selected: null,
		connected: false,
		loading: false,
		error: false,
		matters: [],
		filters: null,
		downloadingCSV: false,
	},
})
@Injectable()
export class MattersListOverviewState implements NgxsOnInit {
	private loggedInUserRole: BusinessUserRole;
	private loggedInUserId: string;
	constructor(
		private store: Store,
		private matterFilterFunctions: MatterFilterFunctions,
		private snackbarService: SnackbarService,
	) {}

	@Selector([MattersListOverviewState])
	static getError(state: MatterListStateModel): boolean {
		return state?.error;
	}

	@Selector([MattersListOverviewState])
	static getCSVDownloading(state: MatterListStateModel): boolean {
		return state?.downloadingCSV;
	}

	@Selector([MattersListOverviewState])
	static getConnected(state: MatterListStateModel): boolean {
		return state?.connected;
	}

	@Selector([MattersListOverviewState])
	static getMatters(state: MatterListStateModel) {
		return state?.matters;
	}

	@Selector([MattersListOverviewState])
	static matters(state: MatterListStateModel): Matter[] {
		return state?.matters;
	}

	@Selector([MattersListOverviewState])
	static getLoading(state: MatterListStateModel): boolean {
		return state?.loading;
	}

	@Selector([MattersListOverviewState])
	static getFilters(state: MatterListStateModel): Filters {
		return state?.filters;
	}

	ngxsOnInit(sc: StateContext<MatterListStateModel>): void {
		this.store.select(BusinessState.businessId).subscribe((id) => {
			sc.setState({
				selected: null,
				connected: !!id,
				loading: false,
				error: false,
				matters: [],
				filters: this.getDefaultFilters(),
				downloadingCSV: false,
			});
		});

		this.store
			.select(BusinessUsersState.getLoggedInBusinessUser)
			.pipe(filter((user) => !!user))
			.subscribe(({ role, id, restricted }) => {
				this.loggedInUserRole = role;
				this.loggedInUserId = id;

				if (!restricted) return;
				if (role === BusinessUserRole.LAWYER) {
					this.store.dispatch(new actions.SetLawyerFilter(id));
				} else if (role === BusinessUserRole.CLERK) {
					this.store.dispatch(new actions.SetClerkFilter(id));
				}
			});
	}

	@Action(actions.StoreConnected)
	storeConnected({ patchState }: StateContext<MatterListStateModel>): void {
		patchState({ connected: true });
	}

	@Action(actions.GetFilteredMatters)
	async getFilteredMatters(
		{ patchState, getState }: StateContext<MatterListStateModel>,
		{}: actions.GetFilteredMatters,
	): Promise<void> {
		const state = getState();
		patchState({ loading: true, error: false });

		try {
			const result = await this.matterFilterFunctions
				.getMatterList(state.filters)
				.pipe(take(1))
				.toPromise();
			patchState({ matters: result });
		} catch (err) {
			patchState({ matters: [], error: true });
			LogRocket.error('Unable to get filtered matter list', err);
		} finally {
			patchState({ loading: false });
		}
	}

	@Action(actions.SetLawyerFilter)
	async setLawyerFilter(
		{ patchState, dispatch, getState }: StateContext<MatterListStateModel>,
		{ lawyerId }: actions.SetLawyerFilter,
	): Promise<void> {
		const state = getState();

		patchState({ filters: { ...state.filters, lawyer: lawyerId } });
		dispatch(new actions.GetFilteredMatters());
	}

	@Action(actions.SetClerkFilter)
	async setClerkFilter(
		{ patchState, dispatch, getState }: StateContext<MatterListStateModel>,
		{ clerkId }: actions.SetClerkFilter,
	): Promise<void> {
		const state = getState();

		patchState({ filters: { ...state.filters, clerk: clerkId } });
		dispatch(new actions.GetFilteredMatters());
	}

	@Action(actions.SetFileTypeFilter)
	async setFileTypeFilter(
		{ patchState, dispatch, getState }: StateContext<MatterListStateModel>,
		{ fileType }: actions.SetFileTypeFilter,
	): Promise<void> {
		const state = getState();

		patchState({ filters: { ...state.filters, fileType } });
		dispatch(new actions.GetFilteredMatters());
	}

	@Action(actions.SetStatusFilter)
	async setStatusFilter(
		{ patchState, dispatch, getState }: StateContext<MatterListStateModel>,
		{ status }: actions.SetStatusFilter,
	): Promise<void> {
		const state = getState();

		patchState({ filters: { ...state.filters, status } });
		dispatch(new actions.GetFilteredMatters());
	}

	@Action(actions.SetDateFilter)
	async setDateFilter(
		{ patchState, dispatch, getState }: StateContext<MatterListStateModel>,
		{ from, to }: actions.SetDateFilter,
	): Promise<void> {
		const state = getState();

		patchState({ filters: { ...state.filters, fromDate: from, toDate: to } });
		dispatch(new actions.GetFilteredMatters());
	}

	@Action(actions.SetOfficeFilter)
	async setOfficeFilter(
		{ patchState, dispatch, getState }: StateContext<MatterListStateModel>,
		{ businessAddressId }: actions.SetOfficeFilter,
	): Promise<void> {
		const state = getState();

		patchState({ filters: { ...state.filters, office: businessAddressId } });
		dispatch(new actions.GetFilteredMatters());
	}

	@Action(actions.SetSelectedDateFilter)
	async setSelectedDateFilter(
		{ patchState, dispatch, getState }: StateContext<MatterListStateModel>,
		{ selectedDateFilter }: actions.SetSelectedDateFilter,
	): Promise<void> {
		const state = getState();

		patchState({ filters: { ...state.filters, selectedDateFilter } });
		dispatch(new actions.GetFilteredMatters());
	}

	@Action(actions.SetOutstandingUndertaking)
	async setOutstandingUndertaking(
		{ patchState, dispatch, getState }: StateContext<MatterListStateModel>,
		{ outstandingUndertaking }: actions.SetOutstandingUndertaking,
	): Promise<void> {
		const state = getState();

		patchState({
			filters: { ...state.filters, outstandingUndertaking },
		});
		dispatch(new actions.GetFilteredMatters());
	}

	@Action(actions.SetOutstandingRequisition)
	async setOutstandingRequisition(
		{ patchState, dispatch, getState }: StateContext<MatterListStateModel>,
		{ outstandingRequisition }: actions.SetOutstandingRequisition,
	): Promise<void> {
		const state = getState();

		patchState({
			filters: { ...state.filters, outstandingRequisition },
		});
		dispatch(new actions.GetFilteredMatters());
	}

	@Action(actions.ResetFilter)
	async resetFilter(
		{ patchState, dispatch }: StateContext<MatterListStateModel>,
		{}: actions.ResetFilter,
	): Promise<void> {
		patchState({ filters: this.getDefaultFilters() });
		dispatch(new actions.GetFilteredMatters());
	}

	getDefaultFilters(): Filters {
		return {
			clerk:
				this.loggedInUserRole === BusinessUserRole.CLERK
					? this.loggedInUserId
					: null,
			lawyer:
				this.loggedInUserRole === BusinessUserRole.LAWYER
					? this.loggedInUserId
					: null,
			selectedDateFilter: 'closing',
			fromDate: format(startOfWeek(new Date()), 'yyyy-MM-dd'),
			toDate: format(endOfWeek(new Date()), 'yyyy-MM-dd'),
			fileType: null,
			office: null,
			status: null,
			outstandingRequisition: false,
			outstandingUndertaking: false,
		} as Filters;
	}

	@Action(actions.DownloadCSV)
	async downloadCSV(
		{ getState, patchState }: StateContext<MatterListStateModel>,
		{}: actions.DownloadCSV,
	): Promise<void> {
		const { filters } = getState();
		patchState({ downloadingCSV: true });
		try {
			const result = await this.matterFilterFunctions
				.getMatterCSV(filters)
				.pipe(take(1))
				.toPromise();

			this.snackbarService.openSnackBar({
				text: 'Successful downloading CSV',
				type: 'success',
			});
			downloadFile(
				`Closer Report_${filters.fromDate}-${filters.toDate}`,
				result,
				'csv',
			);
		} catch (err) {
			this.snackbarService.openSnackBar({
				text: 'Unable to download report',
				type: 'error',
			});
			LogRocket.error('Unable to download matter list CSV', err);
			console.log('Error downloading csv', err);
		} finally {
			patchState({ downloadingCSV: false });
		}
	}
}
